/**
 * AvatarManager — Settings-oriented avatar management
 * IDENTITY:P2.D
 *
 * Current avatar display (128px), regenerate button with economics,
 * generation history gallery, remove with confirmation.
 */

import React, { useState, useCallback } from 'react';
import { useAvatar } from '../../hooks/useAvatar.js';
import AvatarDisplay from './AvatarDisplay.jsx';
import AvatarGenerator from './AvatarGenerator.jsx';
import AvatarPayment from './AvatarPayment.jsx';

export default function AvatarManager({ user }) {
  const {
    currentAvatar,
    fallback,
    avatarOptions,
    history,
    historyTotal,
    isGenerating,
    isSelecting,
    isLoading,
    error,
    generate,
    select,
    remove,
    refresh,
    loadHistory,
  } = useAvatar();

  const [showGenerator, setShowGenerator] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const isPro = user?.subscription_tier?.tier === 'collector' || user?.subscription_tier?.tier === 'intelligence';

  /* c8 ignore start */
  const handleGenerate = useCallback(async (stylePreset, customPrompt, paymentIntentId) => {
    try {
      await generate(stylePreset, customPrompt, paymentIntentId);
    } catch {
      // Error is handled by the hook
    }
  }, [generate]);

  const handleSelect = useCallback(async (avatarId) => {
    try {
      await select(avatarId);
      setShowGenerator(false);
    } catch {
      // Error handled by hook
    }
  }, [select]);
  /* c8 ignore stop */

  const handleRemove = useCallback(async () => {
    try {
      await remove();
      setShowRemoveConfirm(false);
    /* c8 ignore next 2 */
    } catch {
      // Error handled by hook
    }
  }, [remove]);

  const handleLoadHistory = useCallback(async (page) => {
    setHistoryPage(page);
    await loadHistory(page);
  }, [loadHistory]);

  const handleRegenerateClick = () => {
    if (isPro || !currentAvatar) {
      setShowGenerator(true);
    } else {
      setShowPayment(true);
    }
  };

  const handlePaymentComplete = (paymentIntentId) => {
    setShowPayment(false);
    setShowGenerator(true);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse" role="status" aria-label="Loading avatar">
        <div className="w-32 h-32 rounded-full bg-zinc-800 mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Your Avatar</h2>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg" role="alert">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Current Avatar Display */}
      <div className="flex flex-col items-center gap-4">
        <AvatarDisplay
          avatarUrl={currentAvatar?.image_url}
          size={128}
          fallbackLetter={fallback?.letter || '?'}
          fallbackColor={fallback?.background_color || '#3B1D5E'}
          showBorder
        />

        <div className="flex gap-2">
          <button
            onClick={handleRegenerateClick}
            className="py-2 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white
              hover:bg-zinc-700 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label={currentAvatar ? 'Regenerate avatar' : 'Generate avatar'}
          >
            {currentAvatar ? 'Regenerate' : 'Generate Avatar'}
            {!isPro && currentAvatar && (
              <span className="ml-1 text-orange-400">$2.99</span>
            )}
            {isPro && currentAvatar && (
              <span className="ml-1 text-green-400">Unlimited</span>
            )}
          </button>

          {currentAvatar && (
            <button
              onClick={() => setShowRemoveConfirm(true)}
              className="py-2 px-4 rounded-lg border border-zinc-700 text-sm text-zinc-400
                hover:text-red-400 hover:border-red-800 transition-all
                focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Remove current avatar"
            >
              Remove
            </button>
          )}

          <button
            onClick={() => { setShowHistory(!showHistory); if (!showHistory) handleLoadHistory(1); }}
            className="py-2 px-4 rounded-lg border border-zinc-700 text-sm text-zinc-400
              hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
            aria-label="View avatar history"
          >
            History
          </button>
        </div>
      </div>

      {/* Payment Flow */}
      {showPayment && (
        <AvatarPayment
          onPaymentComplete={handlePaymentComplete}
          onCancel={() => setShowPayment(false)}
        />
      )}

      {/* Generator */}
      {showGenerator && (
        <AvatarGenerator
          onGenerate={handleGenerate}
          onSelect={handleSelect}
          isGenerating={isGenerating}
          options={avatarOptions}
        />
      )}

      {/* Remove Confirmation */}
      {showRemoveConfirm && (
        <div className="p-4 bg-zinc-900 border border-zinc-700 rounded-lg space-y-3" role="dialog" aria-label="Confirm avatar removal">
          <p className="text-sm text-zinc-300">Remove your current avatar? You'll revert to a letter badge.</p>
          <div className="flex gap-2">
            <button
              onClick={handleRemove}
              className="py-2 px-4 rounded-lg bg-red-600 text-white text-sm font-medium
                hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Confirm remove avatar"
            >
              Remove
            </button>
            <button
              onClick={() => setShowRemoveConfirm(false)}
              className="py-2 px-4 rounded-lg border border-zinc-700 text-zinc-400 text-sm
                hover:text-white transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label="Cancel removal"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* History Gallery */}
      {showHistory && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-zinc-400">Generation History</h3>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-600">No avatar history yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2">
                {history.map((avatar) => (
                  <button
                    key={avatar.id}
                    /* c8 ignore next */
                    onClick={() => handleSelect(avatar.id)}
                    className="rounded-lg overflow-hidden border border-zinc-700 hover:border-orange-500
                      transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    aria-label={`Select past avatar from ${new Date(avatar.created_at).toLocaleDateString()}`}
                  >
                    <img
                      src={avatar.thumbnail_url}
                      alt="Past avatar"
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
              {historyTotal > 20 && (
                <div className="flex justify-center gap-2">
                  <button
                    /* c8 ignore next */
                    onClick={() => handleLoadHistory(historyPage - 1)}
                    disabled={historyPage <= 1}
                    className="text-sm text-zinc-400 disabled:text-zinc-700"
                    aria-label="Previous history page"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-zinc-500">Page {historyPage}</span>
                  <button
                    onClick={() => handleLoadHistory(historyPage + 1)}
                    disabled={historyPage * 20 >= historyTotal}
                    className="text-sm text-zinc-400 disabled:text-zinc-700"
                    aria-label="Next history page"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
